import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { getInitialMonth } from '../../utils/format';

function fmt(n) { return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }); }

// Is this the month still in progress right now (IST, matching business timezone)? Generating
// payroll for a month that hasn't ended yet is almost never intentional.
function isCurrentMonth(m) {
  return m === new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7);
}

export default function PayrollPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(getInitialMonth);

  // Follow the global month selector like the rest of the app; the native month input below still
  // lets you reach further back than the global dropdown's 8-month range (needed for back-dated
  // payroll), and picking one there broadcasts the change so the global selector stays in sync
  // whenever the chosen month is within its own range.
  useEffect(() => { const h = e => setMonth(e.detail); window.addEventListener('monthChange', h); return () => window.removeEventListener('monthChange', h); }, []);
  function changeMonth(v) { setMonth(v); window.dispatchEvent(new CustomEvent('monthChange', { detail: v })); }
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedEmps, setSelectedEmps] = useState([]);

  const { data: payroll = [], isLoading, refetch } = useQuery({
    queryKey: ['hr-payroll', month],
    queryFn: () => api.get(`/hr/payroll?month=${month}`).then(r => r.data),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['hr-employees'],
    queryFn: () => api.get('/hr/employees').then(r => r.data),
  });

  const generateMutation = useMutation({
    mutationFn: data => api.post('/hr/payroll/generate', data),
    onSuccess: data => {
      qc.invalidateQueries(['hr-payroll']);
      toast.success(`Payroll generated for ${data.data.count} employee(s)`);
      setShowGenerate(false);
    },
    onError: err => toast.error(err.response?.data?.error || 'Generation failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/hr/payroll/${id}`),
    onSuccess: data => { qc.invalidateQueries(['hr-payroll']); toast.success(data.data.message || 'Deleted'); },
    onError: err => toast.error(err.response?.data?.error || 'Delete failed'),
  });

  function openPayslip(id) {
    api.get(`/hr/payroll/${id}/payslip`, { responseType: 'text' }).then(r => {
      const blob = new Blob([r.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    }).catch(() => toast.error('Failed to open payslip'));
  }

  const totalNet = payroll.reduce((a, p) => a + Number(p.net_salary), 0);
  const totalGross = payroll.reduce((a, p) => a + Number(p.gross_salary), 0);
  const totalDed = payroll.reduce((a, p) => a + Number(p.total_deductions), 0);

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="section-head">
        <h2>Payroll — {new Date(month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="month" value={month} onChange={e => changeMonth(e.target.value)} />
          <button className="btn-primary" onClick={() => setShowGenerate(true)}>⚙ Generate Payroll</button>
        </div>
      </div>

      {payroll.length > 0 && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi"><div className="kpi-label">Employees Processed</div><div className="kpi-val">{payroll.length}</div></div>
          <div className="kpi"><div className="kpi-label">Total Gross</div><div className="kpi-val" style={{ fontSize: 18 }}>{fmt(totalGross)}</div></div>
          <div className="kpi"><div className="kpi-label">Total Deductions</div><div className="kpi-val" style={{ fontSize: 18, color: 'var(--red)' }}>{fmt(totalDed)}</div></div>
          <div className="kpi"><div className="kpi-label">Total Net Payable</div><div className="kpi-val" style={{ fontSize: 18, color: 'var(--accent)' }}>{fmt(totalNet)}</div></div>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Working Days</th>
                <th>Present</th>
                <th>Unpaid</th>
                <th>Gross</th>
                <th>Deductions</th>
                <th>Net Pay</th>
                <th>Payslip</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payroll.map(p => (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.full_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>{p.employee_code}</div>
                  </td>
                  <td style={{ color: 'var(--text2)' }}>{p.department || '—'}</td>
                  <td>{p.working_days}</td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{Number(p.present_days)}</span>
                    {Number(p.paid_leave_days) > 0 && <span style={{ fontSize: 11, color: 'var(--text2)' }}> +{Number(p.paid_leave_days)}L</span>}
                  </td>
                  <td style={{ color: Number(p.unpaid_leave_days) + Number(p.absent_days) > 0 ? 'var(--red)' : undefined }}>
                    {Number(p.unpaid_leave_days) + Number(p.absent_days) || '—'}
                  </td>
                  <td>{fmt(p.gross_salary)}</td>
                  <td style={{ color: 'var(--red)' }}>– {fmt(p.total_deductions)}</td>
                  <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{fmt(p.net_salary)}</td>
                  <td>
                    <button className="btn btn-sm" onClick={() => openPayslip(p.id)}>⬇ Payslip</button>
                  </td>
                  <td>
                    <button className="btn btn-sm" style={{ color:'var(--red)' }}
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete ${p.full_name}'s ${new Date(p.month+'-01').toLocaleString('default',{month:'long',year:'numeric'})} payroll record? This can't be undone from here.`)) {
                          deleteMutation.mutate(p.id);
                        }
                      }}>
                      🗑 Undo
                    </button>
                  </td>
                </tr>
              ))}
              {payroll.length === 0 && (
                <tr>
                  <td colSpan={10} className="empty">
                    No payroll generated for {new Date(month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}.
                    Click "Generate Payroll" to process.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Generate Payroll modal */}
      {showGenerate && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-title">Generate Payroll</div>
            <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:8, padding:'14px 16px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:11, color:'#92400E', fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>You are generating payroll for</div>
              <div style={{ fontSize:20, fontWeight:800, color:'#92400E' }}>{new Date(month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}</div>
            </div>
            {isCurrentMonth(month) && (
              <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#B91C1C', fontWeight:600 }}>
                ⚠ {new Date(month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })} is still in progress — it hasn't ended yet. Payroll is normally generated for a completed month. Did you mean last month instead?
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text2)', marginBottom: 8 }}>
                Select Employees (leave blank for all)
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                {employees.filter(e => e.salary_structure_id).map(e => (
                  <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={selectedEmps.includes(e.id)}
                      onChange={ev => setSelectedEmps(prev => ev.target.checked ? [...prev, e.id] : prev.filter(id => id !== e.id))} />
                    <span style={{ flex: 1 }}>{e.full_name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text2)' }}>{e.salary_structure_name}</span>
                  </label>
                ))}
                {employees.filter(e => !e.salary_structure_id).length > 0 && (
                  <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--orange)', borderTop: '1px solid var(--border)' }}>
                    {employees.filter(e => !e.salary_structure_id).length} employee(s) skipped — no salary structure assigned
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>
                {selectedEmps.length === 0 ? `Processing all ${employees.filter(e => e.salary_structure_id).length} eligible employees` : `${selectedEmps.length} selected`}
              </div>
            </div>

            <div className="card" style={{ background: 'var(--orange-light)', marginBottom: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--orange)' }}>
                Payroll uses attendance data from this month. If a run already exists, it will be recalculated.
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" onClick={() => setShowGenerate(false)}>Cancel</button>
              <button className="btn-primary" disabled={generateMutation.isPending}
                onClick={() => generateMutation.mutate({ month, employee_ids: selectedEmps.length ? selectedEmps : undefined })}>
                {generateMutation.isPending ? 'Generating…' : 'Generate Payroll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
